import type { FlowGraph, FlowNode } from '@whinerp/contracts/flow';
import { Panel, SectionLabel } from '@whinerp/ui';
import { useT } from '../../app/i18n.js';

/**
 * What each "Send an email" step in a journey says (G-058).
 *
 * The step existed with nowhere to write the email, and the engine answered it
 * with "nothing behind it". Now it sends; this is where the words go. A step
 * with nothing written is marked as such here, because the run log will say
 * the same thing and it is better read before the journey goes live.
 *
 * {firstName}, {lastName} and {company} are filled in per person.
 */

const INPUT = 'rounded-control border border-control bg-surface px-3 py-2 text-body text-ink';

export function JourneyEmailPanel({ graph, onChange }: { graph: FlowGraph; onChange: (next: FlowGraph) => void }): JSX.Element | null {
  const t = useT();
  const steps = graph.nodes.filter((node) => node.type === 'send-email');
  if (steps.length === 0) return null;

  const update = (id: string, key: 'subject' | 'body', value: string) =>
    onChange({
      ...graph,
      nodes: graph.nodes.map((node: FlowNode) =>
        node.id === id ? { ...node, config: { ...(node.config ?? {}), [key]: value } } : node,
      ),
    } as FlowGraph);

  return (
    <Panel data-region="journey-emails">
      <div className="flex flex-col gap-3">
        <SectionLabel>{t('journey.emails')}</SectionLabel>
        {/* The merge tokens are passed in as values: the catalogue refuses a
            placeholder left unfilled, which is the guard against "Hi ,". */}
        <p className="text-meta text-ink-meta">
          {t('journey.emails.help', {
            values: { firstName: '{firstName}', lastName: '{lastName}', company: '{company}' },
          })}
        </p>
        {steps.map((step, index) => {
          const config = (step.config ?? {}) as Record<string, string>;
          const empty = !config['subject']?.trim() || !config['body']?.trim();
          return (
            <div key={step.id} className="flex flex-col gap-1" data-journey-email={step.id}>
              <span className="text-dense text-ink">
                {t('journey.emails.step', { values: { n: String(index + 1) } })}
                {empty ? (
                  <span className="ms-2 text-meta text-warning-text" data-journey-email-empty>
                    {t('journey.emails.empty')}
                  </span>
                ) : null}
              </span>
              <label className="flex flex-col gap-1 text-meta text-ink-meta">
                {t('journey.emails.subject')}
                <input className={INPUT} data-journey-email-subject={step.id} value={config['subject'] ?? ''}
                  onChange={(event) => update(step.id, 'subject', event.target.value)} />
              </label>
              <label className="flex flex-col gap-1 text-meta text-ink-meta">
                {t('journey.emails.body')}
                <textarea className={`${INPUT} min-h-24`} data-journey-email-body={step.id} value={config['body'] ?? ''}
                  onChange={(event) => update(step.id, 'body', event.target.value)} />
              </label>
            </div>
          );
        })}
      </div>
    </Panel>
  );
}
